import { useEffect, useState } from 'react';
import { getSuccessfulResultOrThrow } from '@atlassian/commerce-ccp-gateway-client/error-wrappers';
import {
	type OrderDefault,
	// eslint-disable-next-line camelcase
	orderDefaults_get_byOfferingId_v1,
} from '@atlassian/commerce-ccp-gateway-client';
import type { ApplicationEdition } from '@atlassian/jira-shared-types/src/edition.tsx';
import { createInvoiceGroup } from '../services/createInvoiceGroup.tsx';

type UseDefaultOfferingProps = {
	offeringId: string | null;
	entitlementId: string | null;
	transactionAccountId: string | undefined;
	targetEdition: ApplicationEdition;
};

export const useDefaultOffering = ({
	offeringId,
	entitlementId,
	transactionAccountId,
	targetEdition,
}: UseDefaultOfferingProps) => {
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | null>(null);
	const [data, setData] = useState<OrderDefault | null>(null);
	const [invoiceGroupCreated, setInvoiceGroupCreated] = useState<boolean>(false);

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true);

			if (!offeringId || !entitlementId || !transactionAccountId) {
				setLoading(false);
				return;
			}

			try {
				// fetch default order configuration for the target offering
				const orderDefaults = getSuccessfulResultOrThrow(
					await orderDefaults_get_byOfferingId_v1({
						pathParameters: { offeringId },
						queryParameters: {
							currentEntitlementId: entitlementId,
						},

						headers: {
							'X-Transaction-Account': transactionAccountId,
						},
					}),
				);

				const defaultOffering: OrderDefault = orderDefaults;

				// Create an invoice group if it doesn't exist
				if (!defaultOffering.invoiceGroupId?.value && !invoiceGroupCreated) {
					await createInvoiceGroup(transactionAccountId);
					setInvoiceGroupCreated(true);
				}

				setData(defaultOffering);
			} catch (err: unknown) {
				if (err instanceof Error) {
					setError(err);
				} else {
					setError(new Error('Unknown error occurred'));
				}
			}
			setLoading(false);
		};
		if (offeringId && (targetEdition === 'FREE_EDITION' || targetEdition === 'UNLICENSED')) {
			// If the target edition is free or unlicensed, we do not need to fetch the default offering
			setData(null);
			setLoading(false);
			return;
		}
		fetchData();
	}, [offeringId, entitlementId, transactionAccountId, invoiceGroupCreated, targetEdition]);

	return {
		loading,
		error,
		data,
	};
};
